import type { Layer } from './grid';
import {
  burst,
  crescent,
  type Ctx,
  cloud,
  dot,
  drop,
  fistHand,
  flake,
  keyOf,
  M,
  palmHand,
  sabre,
  SCENES,
  type Scene,
  spark,
  speedLines,
  sword,
  yinyang,
} from './scenes';

/**
 * Compositions: any "subject+effect" is a scene (a weapon or hand on the lower left, an effect on
 * the upper right), so the move list gets pictures that differ even inside one school.
 */

const CLAW = ['p..p..p..p', 'kp.kp.kp.k', 'kkkkkkkkkk', 'kkkkkkkkkK', '.kkkkkkkK.', '..wwwwww..'];

const SUBJ: Record<string, Scene> = {
  sword: (L) => sword(L, 2, 21, 13, 10),
  sabre: (L) => sabre(L, 2, 21, 13, 10, M.steel, 2),
  dagger: (L) => sword(L, 3, 20, 11, 12, M.steelMid),
  staff: (L) => {
    L.line(3, 21, 14, 10, M.wood, 2);
    L.rect(1, 21, 3, 2, M.gold);
    L.disc(15, 9, 1.4, M.gold);
  },
  fist: (L) => fistHand(L, 1, 9),
  palm: (L) => palmHand(L, 1, 8),
  claw: (L, x) => L.sprite(2, 10, CLAW, keyOf(x)),
  foot: (L) => {
    L.fillPoly([[2, 13], [10, 13], [13, 18], [13, 20], [2, 20], [2, 15]], M.cloth);
    L.rect(2, 20, 11, 3, M.woodDark);
    L.rect(3, 14, 6, 1, M.steelMid);
  },
  finger: (L) => {
    L.rect(2, 13, 9, 5, M.skin);
    L.rect(9, 11, 9, 2, M.skin);
    L.rect(2, 18, 9, 1, M.skinDark);
    L.rect(2, 19, 6, 2, M.gold);
  },
  none: () => undefined,
};

const EFFECT: Record<string, Scene> = {
  trail: (L, x) => {
    crescent(L, 8, 20, 13, -1.5, -0.1, x.t.hi, x.t.l);
    crescent(L, 8, 20, 10, -1.4, -0.2, x.t.c, x.t.d);
  },
  burst: (L, x) => burst(L, 17, 7, 2, 5, 8, x.t.hi, x.t.l),
  ring: (L, x) => {
    L.arc(10, 14, 9, -1.0, 0.5, x.t.l);
    L.arc(10, 14, 11, -0.9, 0.4, x.t.c);
    spark(L, 20, 4, 1, x.t.hi);
  },
  speed: (L, x) => speedLines(L, [[11, 6, 5], [9, 11, 4], [13, 20, 5]], x.t.l),
  drops: (L, x) => {
    drop(L, 18, 6, M.blood);
    drop(L, 14, 17, M.blood);
    drop(L, 20, 13, x.t.l, x.t.hi);
  },
  flake: (L) => {
    flake(L, 17, 7, 4, M.ice, M.iceDark);
    flake(L, 19, 17, 2, '#ffffff');
  },
  bolt: (L) => L.poly([[20, 1], [15, 7], [19, 8], [14, 14]], M.yellow, 2),
  flame: (L) => {
    L.fillPoly([[17, 2], [20, 7], [21, 12], [17, 14], [14, 11], [15, 6]], M.fire);
    L.fillPoly([[17, 7], [19, 11], [17, 13], [15, 11]], M.yellow);
  },
  smoke: (L, x) => {
    cloud(L, 9, 3, 11, x.t.hi, x.t.c);
    cloud(L, 12, 8, 9, x.t.l, x.t.d);
    cloud(L, 5, 17, 12, x.t.l, x.t.d);
  },
  waves: (L, x) => {
    L.arc(8, 13, 8, -0.9, 0.9, x.t.l);
    L.arc(8, 13, 10, -0.8, 0.8, x.t.c);
    L.arc(8, 13, 12, -0.7, 0.7, x.t.d);
  },
  petals: (L) => {
    for (const [px, py] of [[17, 5], [20, 10], [15, 11], [19, 15], [12, 5]] as const) {
      L.rect(px, py, 2, 2, '#ffb0d8');
      L.set(px, py, '#ff8ac0');
    }
  },
  cracks: (L, x) => {
    L.poly([[12, 14], [10, 17], [13, 19], [10, 22]], x.t.hi, 1);
    L.poly([[12, 14], [16, 17], [15, 20], [20, 22]], x.t.hi, 1);
    L.rect(18, 15, 2, 2, x.t.d);
  },
  stars: (L, x) => {
    spark(L, 18, 6, 2, x.t.hi);
    spark(L, 14, 12, 1, x.t.l);
    spark(L, 20, 14, 1, x.t.l);
  },
  halo: (L) => {
    L.arc(11, 12, 10, 0, 6.3, M.gold, 1);
    burst(L, 11, 12, 10, 12, 12, M.gold, M.goldDark);
  },
  bell: (L) => {
    L.rect(15, 3, 6, 5, M.gold);
    L.rect(14, 8, 8, 2, M.goldDark);
    L.arc(16, 11, 4, -0.8, 0.8, M.gold);
  },
  seal: (L) => {
    L.rect(15, 3, 6, 6, M.red);
    L.rect(16, 4, 4, 4, M.redDark);
    L.rect(17, 5, 2, 2, M.red);
  },
  dew: (L, x) => {
    drop(L, 17, 5, x.t.hi, '#ffffff');
    drop(L, 20, 11, x.t.l, x.t.hi);
    dot(L, 14, 3, x.t.hi);
  },
  eye: (L, x) => {
    L.fillPoly([[12, 4], [16, 2], [21, 4], [16, 7]], M.cloth);
    L.disc(16, 4, 1.5, x.t.c);
  },
  ghost: (L, x) => {
    L.rect(14, 6, 6, 8, x.t.d);
    L.rect(15, 4, 4, 3, x.t.l);
    L.rect(12, 10, 2, 5, x.t.d);
  },
  leaf: (L) => {
    for (const [px, py] of [[17, 5], [20, 10], [14, 3]] as const)
      L.fillPoly([[px, py - 2], [px + 2, py], [px, py + 2], [px - 2, py]], M.jade);
  },
  none: () => undefined,
};

/** "sword+bolt", "fist+cracks"…; returns undefined when a half is unknown. */
export function composite(key: string): Scene | undefined {
  const [a, b] = key.split('+');
  const s = SUBJ[a ?? ''];
  const e = EFFECT[b ?? ''];
  if (!s || !e) return undefined;
  return (L: Layer, x: Ctx) => {
    e(L, x);
    s(L, x);
  };
}

export const COMPOSITE_KEYS: string[] = Object.keys(SUBJ).flatMap((a) =>
  Object.keys(EFFECT).map((b) => `${a}+${b}`),
);
for (const k of COMPOSITE_KEYS) SCENES[k] = composite(k)!;

const extra: Record<string, Scene> = {
  bamboo: (L, x) => {
    for (const [px, h] of [[6, 17], [11, 19], [16, 15]] as const) {
      L.rect(px, 21 - h, 3, h, M.jade);
      L.rect(px, 21 - h, 1, h, '#2a8a4a');
      for (let y = 21 - h + 4; y < 21; y += 5) L.rect(px - 1, y, 5, 1, '#1f6a3a');
    }
    L.fillPoly([[17, 4], [21, 3], [19, 7]], M.jade);
    spark(L, 4, 5, 1, x.t.hi);
  },
  yinBreak: (L) => {
    yinyang(L, 12, 12, 7);
    L.poly([[3, 20], [10, 13], [8, 11], [16, 4], [14, 2], [21, 1]], null, 2);
    sword(L, 3, 21, 12, 12);
  },
  yinFocus: (L, x) => {
    L.arc(12, 12, 9, 0, 6.3, x.t.hi, 1);
    yinyang(L, 12, 12, 4);
    for (const [a, b, c, d] of [[12, 1, 12, 4], [12, 20, 12, 23], [1, 12, 4, 12], [20, 12, 23, 12]] as const) L.line(a, b, c, d, x.t.l);
  },
  taijiBeam: (L, x) => {
    yinyang(L, 12, 17, 5);
    L.rect(11, 1, 3, 10, x.t.hi);
    L.rect(12, 1, 1, 10, '#ffffff');
    sword(L, 12, 11, 12, 1);
  },
  sealGold: (L) => {
    L.arc(12, 12, 10, 0, 6.3, M.gold, 1);
    L.rect(6, 6, 12, 12, M.gold);
    L.rect(7, 7, 10, 10, M.goldDark);
    L.rect(8, 8, 8, 8, M.gold);
    L.rect(10, 10, 4, 4, M.red);
    L.rect(11, 3, 2, 3, M.redDark);
  },
  cloudDrift: (L, x) => {
    cloud(L, 3, 13, 14, x.t.hi, x.t.c);
    cloud(L, 8, 8, 12, x.t.l, x.t.d);
    L.poly([[12, 4], [15, 1], [18, 4]], x.t.hi, 1);
    cloud(L, 5, 18, 16, x.t.l, x.t.d);
  },
  mist: (L, x) => {
    for (let i = 0; i < 4; i++) L.curve(2, 5 + i * 5, 9, 1 + i * 5, 14 + (i % 2) * 4, 5 + i * 5, i % 2 ? x.t.c : x.t.l, 2);
    spark(L, 19, 20, 1, x.t.hi);
  },
};
Object.assign(SCENES, extra);
void sabre;
